import { Fragment, useMemo, useRef, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    closestCenter,
    pointerWithin,
    useSensor,
    useSensors,
    type CollisionDetection,
    type DragEndEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { KanbanColumnView } from './kanban-column';
import { columnIdFromDroppable, columnSortableId } from './kanban-utils';
import type { KanbanBoardProps, KanbanItem } from './kanban-types';
import { useColumnFlip } from './use-column-flip';

/**
 * Generic kanban board on dnd-kit. Consumers own the data (columns, items,
 * cards, move mutation) — the board owns drag mechanics, drop validation
 * feedback, and per-column load-more. Used by module records (SELECT-field
 * grouping) and tickets (status columns).
 */
export function KanbanBoard<T extends KanbanItem>({
    columns,
    items,
    renderCard,
    canDrop,
    onMove,
    onLoadMore,
    loadingMoreColumnId,
    disabled,
    renderColumnHeader,
    renderColumnFooter,
    renderEmptyColumn,
    reorderableColumnIds,
    onReorderColumns,
    trailing,
    trailingAfterColumnId,
}: KanbanBoardProps<T>) {
    const [activeId, setActiveId] = useState<string | null>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    useColumnFlip(scrollerRef, columns.map((column) => column.id).join('|'));
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const itemsByColumn = useMemo(() => {
        const map = new Map<string, T[]>();
        for (const column of columns) map.set(column.id, []);
        for (const item of items) {
            const bucket = map.get(item.columnId);
            if (bucket) bucket.push(item);
        }
        return map;
    }, [columns, items]);

    const sortableIds = useMemo(
        () => (reorderableColumnIds ?? []).map((id) => columnSortableId(id)),
        [reorderableColumnIds],
    );

    const activeItem = activeId ? (items.find((i) => i.id === activeId) ?? null) : null;

    /** Column the `trailing` slot follows; `null` ⇒ after the last column. */
    const trailingAfter =
        trailingAfterColumnId && columns.some((c) => c.id === trailingAfterColumnId)
            ? trailingAfterColumnId
            : null;

    const collisionDetection: CollisionDetection = (args) =>
        args.active.data.current?.type === 'column' ? closestCenter(args) : pointerWithin(args);

    const handleDragStart = (event: DragStartEvent): void => {
        if (event.active.data.current?.type !== 'column') {
            setActiveId(String(event.active.id));
        }
    };

    const handleDragEnd = (event: DragEndEvent): void => {
        setActiveId(null);
        const { active, over } = event;

        if (active.data.current?.type === 'column') {
            if (!over || !reorderableColumnIds || !onReorderColumns) return;
            const ids = [...reorderableColumnIds];
            const from = ids.indexOf(columnIdFromDroppable(active.id));
            const to = ids.indexOf(columnIdFromDroppable(over.id));
            if (from < 0 || to < 0 || from === to) return;
            onReorderColumns(arrayMove(ids, from, to));
            return;
        }

        if (!over) return;
        const item = items.find((i) => i.id === String(active.id));
        if (!item) return;
        const toColumnId = columnIdFromDroppable(over.id);
        if (toColumnId === item.columnId) return;
        if (canDrop && !canDrop(item, toColumnId)) return;
        onMove(item, toColumnId);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
        >
            <div
                ref={scrollerRef}
                className="scrollbar-thin flex h-full min-h-0 gap-4 overflow-x-auto pb-4"
            >
                <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
                    {columns.map((column) => (
                        <Fragment key={column.id}>
                            <KanbanColumnView
                                column={column}
                                items={itemsByColumn.get(column.id) ?? []}
                                renderCard={renderCard}
                                activeItem={activeItem}
                                canDrop={canDrop}
                                onLoadMore={onLoadMore}
                                isLoadingMore={loadingMoreColumnId === column.id}
                                disabled={disabled}
                                renderColumnHeader={renderColumnHeader}
                                renderColumnFooter={renderColumnFooter}
                                renderEmptyColumn={renderEmptyColumn}
                                reorderable={reorderableColumnIds?.includes(column.id) ?? false}
                            />
                            {trailingAfter === column.id ? trailing : null}
                        </Fragment>
                    ))}
                </SortableContext>
                {trailingAfter === null ? trailing : null}
            </div>
            {/* No drop animation: the default flies the card back to its source
                column before the move lands, which reads as a glitch. */}
            <DragOverlay dropAnimation={null}>
                {activeItem ? (
                    <div className="w-[296px] rotate-1 rounded-lg shadow-md">
                        {renderCard(activeItem)}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
